import React, { useEffect, useState } from "react";
import axios from "axios";
import { CSVLink } from "react-csv";
import { Button, Modal, Input, Form, message } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';

const WorkerRegistrationManagement = () => {
  const [formResponses, setFormResponses] = useState([]);
  const apiUrl = import.meta.env.VITE_API_URL;
  const [formFields, setFormFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [deletingResponse, setDeletingResponse] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [content, setContent] = useState(null);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [contentForm] = Form.useForm();

  // Fetch content from API
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${apiUrl}/form-info/worker-registration/`);
        setContent(response.data);
      } catch (error) {
        console.error("Error fetching content:", error);
        message.error("Failed to load content");
      }
    };

    fetchContent();
  }, [apiUrl]);

  // Handle opening content editor modal
  const handleOpenContentEditor = () => {
    if (content) {
      contentForm.setFieldsValue(content);
    }
    setIsContentModalOpen(true);
  };

  // Handle saving content
  const handleSaveContent = async () => {
    try {
      const values = await contentForm.validateFields();
      await axios.patch(`${apiUrl}/form-info/worker-registration/`, values);
      setContent(values);
      setIsContentModalOpen(false);
      message.success("Content updated successfully!");
    } catch (error) {
      console.error("Error updating content:", error);
      message.error("Failed to update content");
    }
  };

  useEffect(() => {
    const fetchPaymentAmount = async () => {
      try {
        const response = await axios.get(`${apiUrl}/forms/2`);
        setPaymentAmount(response.data.payment_amount || "");
      } catch (error) {
        setIsLoading(false);
      }
    };

    fetchPaymentAmount();
  }, [apiUrl]);

  const handleUpdatePaymentAmount = async () => {
    const formattedAmount = parseFloat(paymentAmount).toFixed(2);
  
    try {
      await axios.patch(`${apiUrl}/forms/2/`, {
        payment_amount: formattedAmount,
      });
      message.success("Payment amount updated successfully!");
    } catch (error) {
      console.error("Error updating payment amount:", error);
      message.error("Failed to update payment amount");
    }
  };

  const handleDeleteResponse = (responseId) => {
    setDeletingResponse(responseId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${apiUrl}/form_responses/${deletingResponse}/`);
      setFormResponses(formResponses.filter((response) => response.id !== deletingResponse));
      setIsDeleteModalOpen(false);
      message.success("Response deleted successfully!");
    } catch (error) {
      console.error("Error deleting form response:", error);
      message.error("Failed to delete response");
    }
  };

  const [newField, setNewField] = useState({
    name: "",
    field_type: "text",
    required: false,
    options: [],
    priority: 0,
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);

  // Fetch fields from the API
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await axios.get(`${apiUrl}/form-fields/2/`);
        const sortedFields = response.data.sort((a, b) => a.priority - b.priority);
        setFormFields(sortedFields);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching form fields:", error);
        setErrorMessage("Failed to fetch form fields");
        setIsLoading(false);
      }
    };

    fetchFields();
  }, [apiUrl]);

  // Handle adding a new field
  const handleAddField = async () => {
    if (!newField.name) {
      message.warning("Field name is required");
      return;
    }

    if (["select", "radio", "checkbox"].includes(newField.field_type) && newField.options.length === 0) {
      message.warning("Please provide options for this field type");
      return;
    }

    try {
      const payload = {
        ...newField,
        form: 2,
      };

      const response = await axios.post(`${apiUrl}/form-fields/2/`, payload);
      setFormFields([...formFields, response.data]);
      setNewField({ name: "", field_type: "text", required: false, options: [], priority: 0 });
      message.success("Field added successfully!");
    } catch (error) {
      console.error("Error adding field:", error.response?.data || error);
      message.error("Failed to add field");
    }
  };

  // Handle editing an existing field
  const handleEditField = (field) => {
    setEditingField(field);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await axios.patch(`${apiUrl}/form-fields/2/${editingField.id}/`, editingField);
      setFormFields(formFields.map((field) => (field.id === editingField.id ? response.data : field)));
      setEditModalOpen(false);
      message.success("Field updated successfully!");
    } catch (error) {
      console.error("Error updating field:", error);
      message.error("Failed to update field");
    }
  };

  // Handle deleting a field
  const handleDeleteField = async (fieldId) => {
    try {
      await axios.delete(`${apiUrl}/form-fields/2/${fieldId}/`);
      setFormFields(formFields.filter((field) => field.id !== fieldId));
      message.success("Field deleted successfully!");
    } catch (error) {
      console.error("Error deleting field:", error);
      message.error("Failed to delete field");
    }
  };

  // Handle moving a field up or down
  const handleMoveField = async (fieldId, direction) => {
    const fieldIndex = formFields.findIndex((field) => field.id === fieldId);
    if (
      (direction === "up" && fieldIndex === 0) ||
      (direction === "down" && fieldIndex === formFields.length - 1)
    ) {
      return;
    }
  
    const updatedFields = [...formFields];
    const newIndex = direction === "up" ? fieldIndex - 1 : fieldIndex + 1;
    [updatedFields[fieldIndex], updatedFields[newIndex]] = [
      updatedFields[newIndex],
      updatedFields[fieldIndex],
    ];
  
    updatedFields.forEach((field, index) => {
      field.priority = index;
    });
  
    try {
      await axios.patch(`${apiUrl}/form-fields/2/${fieldId}/`, {
        priority: newIndex,
      });
  
      await Promise.all(
        updatedFields.map((field) =>
          axios.patch(`${apiUrl}/form-fields/2/${field.id}/`, {
            priority: field.priority,
          })
        )
      );
  
      setFormFields(updatedFields);
    } catch (error) {
      console.error("Error updating priority:", error);
      message.error("Failed to update priority");
    }
  };

  // Fetch form responses
  useEffect(() => {
    const fetchFormResponses = async () => {
      try {
        const response = await axios.get(`${apiUrl}/form_responses/?form_id=2`);
        const sortedResponses = (response.data || []).sort(
          (a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)
        );
        setFormResponses(sortedResponses);
      } catch (error) {
        console.error("Error fetching form responses:", error);
        setErrorMessage("Failed to fetch responses");
      }
    };

    fetchFormResponses();
  }, [apiUrl]);

  // Prepare CSV data
  const csvData = formResponses.map((response) => {
    const row = { "Submitted At": new Date(response.submitted_at).toLocaleString() };
    formFields.forEach((field) => {
      const fieldResponse = response.field_responses.find((fr) => fr.form_field.id === field.id);
      row[field.name] = fieldResponse
        ? field.field_type === "image"
          ? fieldResponse.file
          : fieldResponse.value || "N/A"
        : "N/A";
    });
    return row;
  });

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-4xl font-bold mb-6">Worker Registration Management</h1>

      {/* Content Editor Button */}
      <div className="mb-6 flex gap-4">
        <Button 
          type="primary" 
          onClick={handleOpenContentEditor}
          className="bg-blue-600 text-white"
        >
          Edit Page Content
        </Button>
      </div>

      {/* Payment Amount Section */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Set Payment Amount</h2>
        <div className="flex items-center gap-4">
          <Input
            type="number"
            placeholder="Payment Amount"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            style={{ width: 200 }}
          />
          <Button
            type="primary"
            onClick={handleUpdatePaymentAmount}
          >
            Save
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p>Loading form fields...</p>
      ) : errorMessage ? (
        <p className="text-red-500">{errorMessage}</p>
      ) : (
        <>
          <h2 className="text-3xl font-bold mb-4">Edit Form</h2>
          
          {/* Display Existing Fields */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Form Fields</h2>
            {formFields.length === 0 ? (
              <p>No fields available.</p>
            ) : (
              <ul>
                {formFields.map((field, index) => (
                  <li key={field.id} className="flex justify-between items-center mb-4 border-b pb-2">
                    <div>
                      <p className="font-bold">{field.name}</p>
                      <p className="text-sm text-gray-500">
                        Type: {field.field_type} | Priority: {field.priority} | Required: {field.required ? "Yes" : "No"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        icon={<ArrowUpOutlined />}
                        onClick={() => handleMoveField(field.id, "up")}
                        disabled={index === 0}
                      />
                      <Button
                        icon={<ArrowDownOutlined />}
                        onClick={() => handleMoveField(field.id, "down")}
                        disabled={index === formFields.length - 1}
                      />
                      <Button
                        type="default"
                        onClick={() => handleEditField(field)}
                      >
                        Edit
                      </Button>
                      <Button
                        danger
                        onClick={() => handleDeleteField(field.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Add New Field Section */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Add New Field</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Field Name</label>
              <Input
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Field Type</label>
              <select
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-blue-500"
                value={newField.field_type}
                onChange={(e) => setNewField({ ...newField, field_type: e.target.value })}
              >
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="image">Image</option>
                <option value="textarea">TextArea</option>
                <option value="select">Dropdown</option>
                <option value="checkbox">Checkbox</option>
                <option value="radio">Radio (Choose one)</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <Input
                type="number"
                value={newField.priority}
                onChange={(e) => setNewField({ ...newField, priority: parseInt(e.target.value) })}
              />
            </div>
            {["select", "radio", "checkbox"].includes(newField.field_type) && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                {newField.options.map((option, index) => (
                  <div key={index} className="flex mb-2">
                    <Input
                      value={option}
                      onChange={(e) => {
                        const updatedOptions = [...newField.options];
                        updatedOptions[index] = e.target.value;
                        setNewField({ ...newField, options: updatedOptions });
                      }}
                    />
                    <Button
                      danger
                      className="ml-2"
                      onClick={() => {
                        const updatedOptions = newField.options.filter((_, i) => i !== index);
                        setNewField({ ...newField, options: updatedOptions });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => setNewField({ ...newField, options: [...newField.options, ""] })}
                >
                  Add Option
                </Button>
              </div>
            )}
            <div className="mb-4">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={newField.required}
                  onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                />
                <span className="ml-2">Required</span>
              </label>
            </div>
            <Button
              type="primary"
              onClick={handleAddField}
            >
              Add Field
            </Button>
          </div>

          {/* Form Responses Section */}
          <h2 className="text-3xl font-bold my-4">Form Responses</h2>
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            {formResponses.length === 0 ? (
              <p>No form responses available.</p>
            ) : (
              <>
                <CSVLink data={csvData} filename="form_responses.csv">
                  <Button type="primary" className="mb-4">
                    Export to CSV
                  </Button>
                </CSVLink>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-4 border-b">Submitted At</th>
                        {formFields.map((field) => (
                          <th key={field.id} className="py-2 px-4 border-b">
                            {field.name}
                          </th>
                        ))}
                        <th className="py-2 px-4 border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formResponses.map((response) => (
                        <tr key={response.id}>
                          <td className="py-2 px-4 border-b">
                            {new Date(response.submitted_at).toLocaleString()}
                          </td>
                          {formFields.map((field) => {
                            const fieldResponse = response.field_responses.find((fr) => fr.form_field.id === field.id);
                            return (
                              <td key={field.id} className="py-2 px-4 border-b">
                                {fieldResponse ? (
                                  field.field_type === "image" ? (
                                    <>
                                      <img
                                        src={fieldResponse.file}
                                        className="w-12 h-12 object-cover rounded-full mb-2"
                                        alt={field.name}
                                      />
                                      <a
                                        href={fieldResponse.file}
                                        download
                                        className="text-blue-500 hover:underline"
                                      >
                                        Download
                                      </a>
                                    </>
                                  ) : fieldResponse.value || "N/A"
                                ) : (
                                  "N/A"
                                )}
                              </td>
                            );
                          })}
                          <td className="py-2 px-4 border-b">
                            <Button
                              danger
                              onClick={() => handleDeleteResponse(response.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Edit Field Modal */}
      {editModalOpen && (
        <Modal
          title="Edit Field"
          open={editModalOpen}
          onOk={handleSaveEdit}
          onCancel={() => setEditModalOpen(false)}
        >
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Field Name</label>
            <Input
              value={editingField?.name}
              onChange={(e) => setEditingField({ ...editingField, name: e.target.value })}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Field Type</label>
            <select
              className="w-full border border-gray-300 rounded-md p-2"
              value={editingField?.field_type}
              onChange={(e) => setEditingField({ ...editingField, field_type: e.target.value })}
            >
              <option value="text">Text</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="image">Image</option>
              <option value="textarea">TextArea</option>
              <option value="select">Dropdown</option>
              <option value="checkbox">Checkbox</option>
              <option value="radio">Radio (Single Choice)</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <Input
              type="number"
              value={editingField?.priority}
              onChange={(e) => setEditingField({ ...editingField, priority: parseInt(e.target.value) })}
            />
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={isDeleteModalOpen}
        onOk={handleConfirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
      >
        <p>Are you sure you want to delete this form response?</p>
      </Modal>

      {/* Content Editor Modal */}
      <Modal
        title="Edit Page Content"
        open={isContentModalOpen}
        onOk={handleSaveContent}
        onCancel={() => setIsContentModalOpen(false)}
        width={800}
      >
        <Form form={contentForm} layout="vertical">
          <Form.Item name="heroTitle" label="Hero Title">
            <Input />
          </Form.Item>
          <Form.Item name="heroSubtitle" label="Hero Subtitle">
            <Input />
          </Form.Item>
          <Form.Item name="whyRegisterTitle" label="Why Register Title">
            <Input />
          </Form.Item>
          
          <h3 className="text-lg font-bold mb-2">Benefits</h3>
          <Form.List name="benefits">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} className="mb-4 border-b pb-4">
                    <Form.Item
                      {...restField}
                      name={[name, 'title']}
                      label="Title"
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'description']}
                      label="Description"
                    >
                      <Input.TextArea />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'icon']}
                      label="Icon"
                    >
                      <Input placeholder="check, money, or shield" />
                    </Form.Item>
                    <Button danger onClick={() => remove(name)}>
                      Remove Benefit
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  Add Benefit
                </Button>
              </>
            )}
          </Form.List>

          <Form.Item name="feeTitle" label="Fee Title">
            <Input />
          </Form.Item>
          <Form.Item name="feeDescription" label="Fee Description">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="paymentAcceptedText" label="Payment Accepted Text">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkerRegistrationManagement;